import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

/**
 * Envelope that travels over the wire. Everything inside is ciphertext / base64,
 * so the browser Network tab only ever shows opaque blobs — never the real
 * request parameters or the real response body.
 *
 *  keyId : (request only) id of the ephemeral SERVER key this request was
 *          encrypted to — the server validates it was one it just issued
 *  k     : RSA-OAEP(SHA-256) encrypted AES-256 session key
 *  iv    : 12-byte AES-GCM initialisation vector
 *  ct    : AES-256-GCM ciphertext WITH the 16-byte auth tag appended
 *  pk    : (request only) the browser's ephemeral RSA public key (SPKI/DER, base64)
 */
export interface SecureEnvelope {
  keyId?: string;
  k: string;
  iv: string;
  ct: string;
  pk?: string;
}

@Injectable({ providedIn: 'root' })
export class CryptoService {

  /** Browser-side ephemeral RSA key pair, generated once on page load. */
  private clientKeyPairPromise!: Promise<CryptoKeyPair>;
  /** SPKI/DER (base64) form of the client public key — sent with every request. */
  private clientPublicKeyB64Promise!: Promise<string>;
  /** Ephemeral server key for this page load: its imported CryptoKey + the keyId to echo back. */
  private serverKeyPromise: Promise<{ key: CryptoKey; keyId: string }> | null = null;

  constructor(private http: HttpClient) {
    // Generate the first ephemeral key pair as soon as the service is created.
    this.regenerate();
  }

  /**
   * Generate a brand-new ephemeral RSA key pair, discarding the previous one.
   * Call this on every page/component load so each session uses a unique key
   * (a full browser reload re-runs the constructor and does this automatically;
   * call it explicitly to also get a fresh key on SPA route navigation).
   */
  regenerate(): void {
    // Drop the cached server handshake so the next request fetches a fresh
    // ephemeral server key (new keyId) for this page load.
    this.serverKeyPromise = null;

    this.clientKeyPairPromise = crypto.subtle.generateKey(
      {
        name: 'RSA-OAEP',
        modulusLength: 2048,
        publicExponent: new Uint8Array([1, 0, 1]),
        hash: 'SHA-256'
      },
      true,
      ['encrypt', 'decrypt']
    ) as Promise<CryptoKeyPair>;

    this.clientPublicKeyB64Promise = this.clientKeyPairPromise.then(async (pair) => {
      const spki = await crypto.subtle.exportKey('spki', pair.publicKey);
      const b64 = this.bufToB64(spki);
      // Log a short fingerprint so you can SEE it change on every page load.
      // (The base64 itself shares a long identical ASN.1 prefix across all
      //  RSA-2048 keys, so eyeballing the start is misleading — this isn't.)
      const fp = await crypto.subtle.digest('SHA-256', spki);
      const fpHex = Array.from(new Uint8Array(fp))
        .slice(0, 8)
        .map((x) => x.toString(16).padStart(2, '0'))
        .join('');
      console.log('🔑 New client public key generated. Fingerprint:', fpHex);
      return b64;
    });
  }

  /**
   * Encrypt an arbitrary JSON-serialisable payload into a SecureEnvelope.
   * The server can decrypt it with its private key, and will use the included
   * client public key (`pk`) to encrypt its response back to us.
   */
  async encryptRequest(payload: any, baseUrl: string): Promise<SecureEnvelope> {
    const server = await this.getServerPublicKey(baseUrl);
    const clientPublicKeyB64 = await this.clientPublicKeyB64Promise;

    // 1. Random one-time AES-256 session key + IV.
    const aesKey = await crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt', 'decrypt']
    );
    const iv = crypto.getRandomValues(new Uint8Array(12));

    // 2. Encrypt the real payload with AES-GCM (output already includes the auth tag).
    const plaintext = new TextEncoder().encode(JSON.stringify(payload));
    const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, aesKey, plaintext);

    // 3. Encrypt the AES key with the ephemeral SERVER public key (only the server can read it).
    const rawAes = await crypto.subtle.exportKey('raw', aesKey);
    const encKey = await crypto.subtle.encrypt({ name: 'RSA-OAEP' }, server.key, rawAes);

    return {
      keyId: server.keyId,
      k: this.bufToB64(encKey),
      iv: this.bufToB64(iv.buffer),
      ct: this.bufToB64(ct),
      pk: clientPublicKeyB64
    };
  }

  /**
   * Decrypt a SecureEnvelope returned by the server. The AES key inside was
   * encrypted with OUR public key, so only our in-memory private key can open it.
   */
  async decryptResponse(env: SecureEnvelope): Promise<any> {
    const pair = await this.clientKeyPairPromise;

    // 1. Recover the AES session key with our private key.
    const rawAes = await crypto.subtle.decrypt(
      { name: 'RSA-OAEP' },
      pair.privateKey,
      this.b64ToBuf(env.k)
    );
    const aesKey = await crypto.subtle.importKey(
      'raw',
      rawAes,
      { name: 'AES-GCM' },
      false,
      ['decrypt']
    );

    // 2. Decrypt the actual response body (GCM verifies the auth tag).
    const pt = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: new Uint8Array(this.b64ToBuf(env.iv)) },
      aesKey,
      this.b64ToBuf(env.ct)
    );
    return JSON.parse(new TextDecoder().decode(pt));
  }

  /** Handshake: fetch the ephemeral server public key + keyId once per page load, then cache. */
  private getServerPublicKey(baseUrl: string): Promise<{ key: CryptoKey; keyId: string }> {
    if (!this.serverKeyPromise) {
      this.serverKeyPromise = (async () => {
        const res = await firstValueFrom(
          this.http.get<{ keyId: string; publicKey: string }>(baseUrl + 'crypto/publicKey')
        );
        const der = this.pemToBuf(res.publicKey);
        const key = await crypto.subtle.importKey(
          'spki',
          der,
          { name: 'RSA-OAEP', hash: 'SHA-256' },
          false,
          ['encrypt']
        );
        return { key, keyId: res.keyId };
      })().catch((err) => {
        // Allow a later retry if the first fetch fails.
        this.serverKeyPromise = null;
        throw err;
      });
    }
    return this.serverKeyPromise;
  }

  // ---------- encoding helpers ----------

  private bufToB64(buf: ArrayBuffer): string {
    const bytes = new Uint8Array(buf);
    let bin = '';
    for (let i = 0; i < bytes.length; i++) {
      bin += String.fromCharCode(bytes[i]);
    }
    return btoa(bin);
  }

  private b64ToBuf(b64: string): ArrayBuffer {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) {
      bytes[i] = bin.charCodeAt(i);
    }
    return bytes.buffer;
  }

  private pemToBuf(pem: string): ArrayBuffer {
    const body = pem
      .replace(/-----BEGIN [^-]+-----/, '')
      .replace(/-----END [^-]+-----/, '')
      .replace(/\s+/g, '');
    return this.b64ToBuf(body);
  }
}
